import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { DayEndClosure, DenominationData } from '@/app/types/dayEndClosure';
import { format } from 'date-fns';
import { IndianRupee, Calendar, User, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

interface ClosureDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  closure: DayEndClosure | null;
}

export const ClosureDetailDialog: React.FC<ClosureDetailDialogProps> = ({
  open,
  onOpenChange,
  closure
}) => {
  if (!closure) return null;

  // Debug logging
  console.log('Closure data:', closure);
  console.log('Opening denomination:', closure.opening_denomination);
  console.log('Closing denomination:', closure.closing_denomination);

  const renderDenominations = (denominations: DenominationData | undefined, title: string) => {
    if (!denominations || Object.keys(denominations).length === 0) {
      return (
        <div className="text-sm text-muted-foreground">No denomination data available</div>
      );
    }

    const sortedDenoms = Object.entries(denominations).sort((a, b) => parseInt(b[0]) - parseInt(a[0]));
    const total = sortedDenoms.reduce((sum, [denom, count]) => sum + (parseInt(denom) * count), 0);

    return (
      <div className="space-y-2">
        <h4 className="font-medium text-sm">{title}</h4>
        <div className="grid grid-cols-3 gap-2 text-sm">
          {sortedDenoms.map(([denom, count]) => (
            <div key={denom} className="flex justify-between items-center p-2 bg-muted rounded">
              <span className="flex items-center gap-1">
                <IndianRupee className="w-3 h-3" />
                {denom}
              </span>
              <span>× {count}</span>
              <span className="font-medium flex items-center gap-1">
                <IndianRupee className="w-3 h-3" />
                {(parseInt(denom) * count).toLocaleString('en-IN')}
              </span>
            </div>
          ))}
        </div>
        <div className="flex justify-between items-center pt-2 border-t">
          <span className="font-medium">Total:</span>
          <span className="font-semibold flex items-center gap-1">
            <IndianRupee className="w-4 h-4" />
            {total.toLocaleString('en-IN')}
          </span>
        </div>
      </div>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'not_started': { variant: 'secondary' as const, label: 'Not Started', icon: Clock },
      'open': { variant: 'default' as const, label: 'Open', icon: Clock },
      'closing': { variant: 'outline' as const, label: 'Closing', icon: Clock },
      'closed': { variant: 'success' as const, label: 'Closed', icon: CheckCircle }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.not_started;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount: string | undefined) => {
    if (!amount) return '—';
    return parseFloat(amount).toLocaleString('en-IN');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Day-End Closure Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header Info */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-semibold">
                {format(new Date(closure.business_date), 'EEEE, dd MMMM yyyy')}
              </p>
              <p className="text-sm text-muted-foreground">
                Site: {closure.site_name || `Site ID: ${closure.site_id}`}
              </p>
            </div>
            <div className="text-right">
              {getStatusBadge(closure.status)}
              {closure.is_reconciled && (
                <Badge variant="success" className="mt-2">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Reconciled
                </Badge>
              )}
            </div>
          </div>

          <Separator />

          {/* Opening Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Opening Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Software Balance</p>
                  <p className="text-xl font-semibold flex items-center gap-1">
                    <IndianRupee className="w-4 h-4" />
                    {formatCurrency(closure.opening_balance_software)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Physical Balance</p>
                  <p className="text-xl font-semibold flex items-center gap-1">
                    <IndianRupee className="w-4 h-4" />
                    {formatCurrency(closure.opening_balance_physical)}
                  </p>
                </div>
              </div>

              {closure.opening_discrepancy && parseFloat(closure.opening_discrepancy) !== 0 && (
                <div className={`p-3 rounded-lg ${
                  parseFloat(closure.opening_discrepancy) < 0 ? 'bg-destructive/5' : 'bg-warning/5'
                }`}>
                  <p className="text-sm font-medium flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Opening Discrepancy: 
                    <span className={`font-semibold ${
                      parseFloat(closure.opening_discrepancy) < 0 ? 'text-destructive' : 'text-warning'
                    }`}>
                      ₹{Math.abs(parseFloat(closure.opening_discrepancy)).toLocaleString('en-IN')}
                      {parseFloat(closure.opening_discrepancy) < 0 ? ' (Shortage)' : ' (Overage)'}
                    </span>
                  </p>
                  {closure.opening_adjustment_voucher_id && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Adjustment Voucher ID: {closure.opening_adjustment_voucher_id}
                    </p>
                  )}
                </div>
              )}

              {closure.opening_denomination && (
                <div className="pt-4">
                  {renderDenominations(closure.opening_denomination, 'Opening Cash Denominations')}
                </div>
              )}

              {closure.opened_at && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2">
                  <Clock className="w-3 h-3" />
                  Opened at {format(new Date(closure.opened_at), 'hh:mm a')}
                  {closure.opened_by_user_id && (
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      by User #{closure.opened_by_user_id}
                    </span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Closing Section */}
          {closure.status === 'closed' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Closing Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Software Balance</p>
                    <p className="text-xl font-semibold flex items-center gap-1">
                      <IndianRupee className="w-4 h-4" />
                      {formatCurrency(closure.closing_balance_software)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Physical Balance</p>
                    <p className="text-xl font-semibold flex items-center gap-1">
                      <IndianRupee className="w-4 h-4" />
                      {formatCurrency(closure.closing_balance_physical)}
                    </p>
                  </div>
                </div>

                {closure.closing_discrepancy && parseFloat(closure.closing_discrepancy) !== 0 && (
                  <div className={`p-3 rounded-lg ${
                    parseFloat(closure.closing_discrepancy) < 0 ? 'bg-destructive/5' : 'bg-warning/5'
                  }`}>
                    <p className="text-sm font-medium flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Closing Discrepancy: 
                      <span className={`font-semibold ${
                        parseFloat(closure.closing_discrepancy) < 0 ? 'text-destructive' : 'text-warning'
                      }`}>
                        ₹{Math.abs(parseFloat(closure.closing_discrepancy)).toLocaleString('en-IN')}
                        {parseFloat(closure.closing_discrepancy) < 0 ? ' (Shortage)' : ' (Overage)'}
                      </span>
                    </p>
                    {closure.closing_adjustment_voucher_id && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Adjustment Voucher ID: {closure.closing_adjustment_voucher_id}
                      </p>
                    )}
                  </div>
                )}

                {closure.closing_denomination && (
                  <div className="pt-4">
                    {renderDenominations(closure.closing_denomination, 'Closing Cash Denominations')}
                  </div>
                )}

                {closure.closed_at && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2">
                    <Clock className="w-3 h-3" />
                    Closed at {format(new Date(closure.closed_at), 'hh:mm a')}
                    {closure.closed_by_user_id && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        by User #{closure.closed_by_user_id}
                      </span>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Summary */}
          {closure.status === 'closed' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Day Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Net Cash Movement</p>
                    <p className="text-xl font-semibold flex items-center justify-center gap-1">
                      <IndianRupee className="w-4 h-4" />
                      {(
                        parseFloat(closure.closing_balance_software || '0') - 
                        parseFloat(closure.opening_balance_software || '0')
                      ).toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Discrepancy</p>
                    <p className={`text-xl font-semibold flex items-center justify-center gap-1 ${
                      parseFloat(closure.closing_discrepancy || '0') < 0 ? 'text-destructive' : 
                      parseFloat(closure.closing_discrepancy || '0') > 0 ? 'text-warning' : 'text-success'
                    }`}>
                      <IndianRupee className="w-4 h-4" />
                      {Math.abs(parseFloat(closure.closing_discrepancy || '0')).toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <div className="mt-1">
                      {closure.is_reconciled ? (
                        <Badge variant="success" className="text-base">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Reconciled
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="text-base">
                          <XCircle className="w-4 h-4 mr-1" />
                          Not Reconciled
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};